'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  DocumentCheckIcon,
  StarIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const services = [
  {
    id: 'svc-iso9001',
    standard: 'ISO 9001:2015',
    title: 'Quality Management',
    description:
      'The world\'s most recognized quality management standard, helping organizations consistently meet customer and regulatory requirements.',
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
  },
  {
    id: 'svc-iso14001',
    standard: 'ISO 14001:2015',
    title: 'Environmental Management',
    description:
      'Framework to help organizations improve environmental performance, fulfill compliance obligations, and achieve environmental objectives.',
    color: 'bg-green-50 border-green-200',
    iconColor: 'text-green-600',
  },
  {
    id: 'svc-iso45001',
    standard: 'ISO 45001:2018',
    title: 'Occupational Safety',
    description:
      'International standard for occupational health and safety management systems to prevent work-related injury, illness, and death.',
    color: 'bg-amber-50 border-amber-200',
    iconColor: 'text-amber-600',
  },
  {
    id: 'svc-iso13485',
    standard: 'ISO 13485:2016',
    title: 'Medical Devices',
    description:
      'Specialized quality management system standard for organizations involved in the design, production, and distribution of medical devices.',
    color: 'bg-red-50 border-red-200',
    iconColor: 'text-red-600',
  },
  {
    id: 'svc-as9100',
    standard: 'ISO 37001 ABMS',
    title: 'Aerospace Quality',
    description:
      'Quality management standard for aerospace, space, and defense industries, building on ISO 9001 with aviation-specific requirements.',
    color: 'bg-purple-50 border-purple-200',
    iconColor: 'text-purple-600',
  },
  {
    id: 'svc-iso27001',
    standard: 'ISO 27001:2022',
    title: 'Information Security',
    description:
      'International standard for information security management systems, protecting sensitive data and managing information security risks.',
    color: 'bg-slate-50 border-slate-200',
    iconColor: 'text-slate-600',
  },
];

const stats = [
  { id: 'stat-certs', value: '1,200+', label: 'Certificates Issued' },
  { id: 'stat-clients', value: '800+', label: 'Certified Organizations' },
  { id: 'stat-years', value: '15+', label: 'Years of Experience' },
  { id: 'stat-rate', value: '100%', label: 'Accreditation Success Rate' },
];

const whyChoose = [
  { id: 'why-1', text: 'Nationally accredited certification body' },
  { id: 'why-2', text: 'Experienced lead auditors across all standards' },
  { id: 'why-3', text: 'Fast turnaround — typically 30 days from application' },
  { id: 'why-4', text: 'Online certificate verification portal for your clients' },
  { id: 'why-5', text: 'Comprehensive audit reports with actionable findings' },
  { id: 'why-6', text: 'Post-certification support and surveillance audit planning' },
];

export default function HomePage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      {/* Hero Section */}
      <section className="gradient-hero pt-24 pb-20 px-6 lg:px-10 relative overflow-hidden">
        {/* Decorative circles */}
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-5 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #D4A017 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-5 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #D4A017 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }}
        />

        <div className="max-w-screen-2xl mx-auto relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 text-blue-200 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-white/20">
              <ShieldCheckIcon className="w-3.5 h-3.5" />
              Accredited ISO Certification Body — Malaysia
            </div>

            <h1 className="text-hero-xl font-bold text-white leading-tight mb-5">
              ISO Certification You Can{' '}
              <span className="text-accent">Trust</span>
            </h1>

            <p className="text-lg text-blue-200 leading-relaxed mb-8 max-w-2xl">
              MOC Certification provides internationally recognized ISO certification
              services. Verify any certificate we have issued instantly using our
              online verification portal.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link href="/certificate-search-page" className="btn-accent px-6 py-3 text-base">
                <MagnifyingGlassIcon className="w-5 h-5" />
                Verify a Certificate
              </Link>
              <a
                href="#services"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold rounded-lg px-6 py-3 text-base hover:bg-white/10 transition-all duration-150"
              >
                Our Services
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Bar */}
      <section className="bg-card border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats?.map(stat => (
              <div key={stat?.id} className="flex flex-col items-center text-center gap-1">
                <span className="text-3xl font-bold text-primary font-tabular">
                  {stat?.value}
                </span>
                <span className="text-sm text-muted-foreground font-medium">
                  {stat?.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Services Section */}
      <section id="services" className="py-16 px-6 lg:px-10">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold text-accent uppercase tracking-widest mb-3">
              Certification Standards
            </span>
            <h2 className="text-section-title font-bold text-foreground mb-3">
              Standards We Certify
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-base">
              We offer certification across all major ISO management system
              standards recognized globally and required by Malaysian regulations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services?.map(svc => (
              <div
                key={svc?.id}
                className={`card border p-6 hover:shadow-elevated transition-all duration-200 ${svc?.color}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2.5 rounded-lg bg-white shadow-sm flex-shrink-0`}>
                    <DocumentCheckIcon className={`w-5 h-5 ${svc?.iconColor}`} />
                  </div>
                  <div>
                    <span className={`text-xs font-bold uppercase tracking-wider ${svc?.iconColor}`}>
                      {svc?.standard}
                    </span>
                    <h3 className="text-base font-semibold text-foreground mt-1 mb-2">
                      {svc?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {svc?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Certificate Verification CTA */}
      <section className="py-12 px-6 lg:px-10 bg-secondary border-y border-border">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-card rounded-2xl p-8 shadow-card border border-border">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-xl gradient-hero flex items-center justify-center flex-shrink-0">
                <ShieldCheckIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">
                  Verify an ISO Certificate
                </h3>
                <p className="text-sm text-muted-foreground">
                  Check the authenticity and validity of any certificate issued by
                  MOC Certification. Enter the certificate ID to get instant results.
                </p>
              </div>
            </div>
            <Link
              href="/certificate-search-page"
              className="btn-primary px-6 py-3 text-base whitespace-nowrap flex-shrink-0"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
              Search Certificate
            </Link>
          </div>
        </div>
      </section>
      {/* About / Why Choose */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-xs font-semibold text-accent uppercase tracking-widest mb-3">
                About MOC Certification
              </span>
              <h2 className="text-section-title font-bold text-foreground mb-4">
                A Trusted Partner for ISO Compliance
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                MOC Certification has been delivering accredited ISO certification
                services in Malaysia since 2018. Our team of experienced lead
                auditors and industry specialists provide thorough, credible audits
                that stand up to international scrutiny.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We understand that certification is not just a document — it is a
                signal of your organization's commitment to quality, safety, and
                continuous improvement. Our process is rigorous, transparent, and
                designed to deliver real value beyond the certificate itself.
              </p>
              <ul className="flex flex-col gap-3">
                {whyChoose?.map(item => (
                  <li key={item?.id} className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-sm text-foreground font-medium">{item?.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { id: 'card-quality', icon: StarIcon, title: 'Accredited Body', desc: 'Recognized by national accreditation authorities', color: 'bg-blue-50' },
                { id: 'card-global', icon: GlobeAltIcon, title: 'Internationally Recognized', desc: 'Certificates accepted by global trading partners', color: 'bg-green-50' },
                { id: 'card-office', icon: BuildingOfficeIcon, title: '800+ Clients', desc: 'Organizations certified across all major industries', color: 'bg-amber-50' },
                { id: 'card-shield', icon: ShieldCheckIcon, title: 'Online Verification', desc: 'Real-time certificate verification portal for your clients', color: 'bg-purple-50' },
              ]?.map(card => (
                <div key={card?.id} className={`card p-5 ${card?.color} border`}>
                  <card.icon className="w-7 h-7 text-primary mb-3" />
                  <h4 className="font-semibold text-foreground text-sm mb-1">{card?.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{card?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section className="py-16 px-6 lg:px-10 bg-secondary border-t border-border">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-section-title font-bold text-foreground mb-3">
              Get in Touch
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Ready to begin your certification journey? Contact our team for a
              consultation and quotation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {[
              { id: 'contact-email', icon: EnvelopeIcon, label: 'Email', value: 'info@moccertification.com' },
              { id: 'contact-phone', icon: PhoneIcon, label: 'Phone', value: '+60175484599' },
              { id: 'contact-office', icon: BuildingOfficeIcon, label: 'Office', value: 'Kuala Lumpur, Malaysia' },
            ]?.map(c => (
              <div key={c?.id} className="card p-5 flex flex-col items-center text-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    {c?.label}
                  </p>
                  <p className="text-sm font-medium text-foreground">{c?.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}